import { useEffect, useRef } from 'react';
import { Link, useRouterState, useNavigate } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import { selectionFeedback } from '@/lib/native';
import { BrandGuard } from '@/brand/BrandGuard';
import { attachEdgeSwipeBack, navigateWithTransition } from '@/lib/transitions';

/**
 * The native shell every screen renders inside: safe-area insets, a scrolling content area, and a
 * fixed bottom tab bar. This is what makes the app read as an app rather than a website in a phone
 * frame, and App Review looks for exactly this (native navigation) — keep the shell, reshape the tabs.
 *
 * Colour and radius come from the brand's CSS variables, so applying a brand re-skins the chrome
 * along with everything else. Nothing here hard-codes an appearance.
 */

export interface MobileTab {
	to: string;
	label: string;
	icon: LucideIcon;
}

export function MobileShell({ tabs, children }: { tabs: MobileTab[]; children: React.ReactNode }) {
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const navigate = useNavigate();
	const shellRef = useRef<HTMLDivElement>(null);

	// Edge-swipe back to the first tab. People reach for this without being told it exists, and its
	// absence is one of the loudest tells that an app is a website in a shell.
	useEffect(() => {
		const el = shellRef.current;
		if (!el || pathname === tabs[0]?.to) return;
		return attachEdgeSwipeBack(el, () => {
			void selectionFeedback();
			navigateWithTransition('back', () => void navigate({ to: tabs[0].to }));
		});
	}, [pathname, tabs, navigate]);

	const tabIndex = tabs.findIndex((t) => t.to === pathname);

	return (
		<div
			ref={shellRef}
			className="flex min-h-dvh flex-col"
			style={{ background: 'var(--brand-bg)', color: 'var(--brand-ink)', fontFamily: 'var(--brand-body)' }}
		>
			{/* Content scrolls under the notch and above the tab bar. pb clears the 4rem bar + inset.
			    The inner max-width does nothing on a phone (it is wider than any handset) and stops the
			    same build from stretching edge-to-edge when it is served as the web app. */}
			<main className="flex-1 overflow-y-auto pt-[env(safe-area-inset-top)] pb-[calc(4rem+env(safe-area-inset-bottom))]">
				<div className="mx-auto w-full max-w-2xl">{children}</div>
			</main>

			<nav
				data-vt="tabbar"
				className="fixed inset-x-0 bottom-0 z-40 border-t pb-[env(safe-area-inset-bottom)] backdrop-blur"
				style={{ borderColor: 'var(--brand-line)', background: 'color-mix(in srgb, var(--brand-bg) 92%, transparent)' }}
			>
				<div className="mx-auto flex h-16 max-w-2xl items-stretch justify-around">
					{tabs.map(({ to, label, icon: Icon }, index) => {
						const active = pathname === to;
						return (
							<Link
								key={to}
								to={to}
								/* Selection, not impact: moving between tabs is navigation, not an action.
								   The transition direction follows tab ORDER, so moving right animates
								   forward and moving left animates back — matching the mental model of a
								   row of tabs rather than a stack. */
								onClick={(e) => {
									void selectionFeedback();
									if (active) return;
									e.preventDefault();
									navigateWithTransition(index > tabIndex ? 'forward' : 'back', () => {
										void navigate({ to });
									});
								}}
								aria-current={active ? 'page' : undefined}
								// 44pt minimum touch target — below this, taps miss on real devices.
								className="flex min-h-[44px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors"
								style={{ color: active ? 'var(--brand-primary)' : 'var(--brand-muted)' }}
							>
								<Icon className="size-5" />
								{label}
							</Link>
						);
					})}
				</div>
			</nav>

			<BrandGuard />
		</div>
	);
}
